import { getService } from 'reducers/service';
import ServiceName from '../../shared/constants/serviceName';
import {
  buildEvidenceRecordsSearchQuery,
  mapEvidenceRecordsSearchResponse,
  mapDisposalRelevantItems,
} from '../utility/evidenceRecordsHelper';
import { EVIDENCE_CUSTODY_FIELDS } from '../constants/evidenceFields';
import { CUSTODY_TRANSACTION_TYPE } from '../constants/evidenceHelper';

export const getIncidentBasedOnIncidentNumber = async (IncidentID) => {
  const service = getService(ServiceName.Inc_Incident);
  const response = await service.find({
    query: {
      IncidentID: IncidentID,
      IsDeleted: false,
      $limit: 1,
      $select: ['IncidentID', 'ptsIncidentID'],
      $sort: {
        Created: -1,
      },
    },
  });
  const data = response?.data || [];
  return data;
};

export const previewTagNumber = async (incidentNumber) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: {
      mode: 'preview-tag-number',
      IncidentID: incidentNumber,
    },
  });

  return response?.TagNumber ?? '';
};

export const createEvidence = async (values) => {
  const service = getService(ServiceName.rms_evidence_detail);
  const response = await service.create(values);
  const data = response?.data || [];
  return data;
};

export const fetchEvidenceOptions = (fieldName, idFieldName) => async ({
  search,
  offset,
  limit,
}) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: {
      field: fieldName,
      idField: idFieldName,
      search: search ?? '',
      $skip: offset,
      $limit: limit,
    },
  });

  return {
    items: (response?.data || []).map((row) => ({
      Id: row.Id,
      Code: row.Code,
      Description: row.Description,
    })),
    totalCount: response?.total || 0,
  };
};

// Collected By's dropdown — distinct CollectedByPersonID values on this agency's evidence,
// resolved to each person's display name via ptsPerson. The dropdown shows the name; the value
// selected/matched is the person ID, not the name (see getCollectedByOptions on the BE).
export const fetchCollectedByOptions = (agencyId) => async ({ search, offset, limit }) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: {
      mode: 'collected-by-options',
      agencyID: agencyId,
      search: search ?? '',
      $skip: offset,
      $limit: limit,
    },
  });

  return {
    items: (response?.data || []).map((row) => ({
      Id: row.Id,
      Code: row.Code,
      Description: row.Description,
    })),
    totalCount: response?.total || 0,
  };
};

export const fetchDistinctFieldOptions = (agencyId, field) => async ({ search, offset, limit }) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: {
      mode: 'field-distinct-options',
      field,
      agencyID: agencyId,
      search: search ?? '',
      $skip: offset,
      $limit: limit,
    },
  });

  return {
    items: (response?.data || []).map((row) => ({
      Id: row.Id,
      Code: row.Code,
      Description: row.Description,
    })),
    totalCount: response?.total || 0,
  };
};

export const searchEvidenceRecords = async ({
  filters = {},
  agencyId,
  page = 0,
  pageSize = 100,
  sortField,
  sortDirection,
}) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: buildEvidenceRecordsSearchQuery({
      filters,
      agencyId,
      sortField,
      sortDirection,
      page,
      pageSize,
    }),
  });

  return mapEvidenceRecordsSearchResponse(response);
};

export const getLocationFilteredEvidence = async ({
  agencyId,
  storageLocationId,
  subLocationId,
  containerId,
  clearanceLevel,
}) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: {
      mode: 'location-filtered',
      agencyID: agencyId,
      StorageLocationID: storageLocationId,
      SubLocationID: subLocationId,
      ContainerID: containerId,
      clearanceLevel,
    },
  });

  return response || [];
};

export const getRandomSampleEvidence = async ({
  agencyId,
  storageLocationId,
  subLocationId,
  containerId,
  sampleSize,
  clearanceLevel,
}) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: {
      mode: 'random-sample',
      agencyID: agencyId,
      StorageLocationID: storageLocationId,
      SubLocationID: subLocationId,
      ContainerID: containerId,
      sampleSize,
      clearanceLevel,
    },
  });

  return response || [];
};

export const getLocationOverdueStatus = async ({ agencyId, overdueDays, clearanceLevel }) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: { mode: 'location-overdue-status', agencyID: agencyId, overdueDays, clearanceLevel },
  });

  return response || [];
};

export const lookupEvidenceById = async ({ agencyId, evidenceId }) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.find({
    query: { mode: 'lookup-by-evidence-id', agencyID: agencyId, EvidenceID: evidenceId },
  });

  return response || null;
};

export const saveAuditTempRecords = async (records) => {
  const service = getService(ServiceName.evidence_audit_temp);
  return service.create(records);
};

export const getAuditTempRecords = async (scannedByID) => {
  const service = getService(ServiceName.evidence_audit_temp);
  const response = await service.find({
    query: { ScannedByID: scannedByID },
  });

  const rows = response?.data || response || [];

  return rows.map((row) => ({
    ...row,
    EvidenceID: row.Evidence?.EvidenceID ?? row.EvidenceID ?? null,
    IncidentID: row.Evidence?.IncidentID ?? row.IncidentID ?? null,
    Description: row.Evidence?.Description ?? row.Description ?? null,
    Category: row.Evidence?.Category ?? row.Category ?? null,
    Status: row.Evidence?.Status ?? row.Status ?? null,
    SubmittedBy: row.Evidence?.SubmittedBy ?? row.SubmittedBy ?? null,
    SubmittedAt: row.Evidence?.SubmittedAt ?? row.SubmittedAt ?? null,
    ScannedBy: row.ScannedByPerson?.FullName ?? row.ScannedBy ?? null,
  }));
};

export const removeAuditTempRecords = async (records) => {
  if (!records?.length) return;
  const service = getService(ServiceName.evidence_audit_temp);
  await service.remove(null, { query: { records } });
};

export const archiveAuditRecords = async (records) => {
  const service = getService(ServiceName.evidence_audit);
  return service.create(records);
};

export const getArchivedAuditRecords = async (agencyId) => {
  const service = getService(ServiceName.evidence_audit);
  const response = await service.find({
    query: { AgencyID: agencyId },
  });

  return response?.data || response || [];
};

export const getEvidenceAuditHistory = async (agencyId, ptsEvidenceID) => {
  const service = getService(ServiceName.evidence_audit);
  const response = await service.find({
    query: { AgencyID: agencyId, ptsEvidenceID },
  });

  return response?.data || response || [];
};

export const fetchAuditedByOptions = (agencyId) => async ({ search, offset, limit }) => {
  const service = getService(ServiceName.evidence_audit);
  const response = await service.find({
    query: {
      mode: 'audited-by-options',
      AgencyID: agencyId,
      search: search ?? '',
      offset,
      limit,
    },
  });

  return {
    items: response?.items || [],
    totalCount: response?.totalCount || 0,
  };
};

export const editEvidence = async (values) => {
  const service = getService(ServiceName.rms_evidence_detail);
  const response = await service.patch(values.ptsEvidenceID, values);
  const data = response?.data || [];
  return data;
};

export const getEvidenceDetails = async (id) => {
  const service = getService(ServiceName.rms_evidence_detail);
  const response = await service.get(id);
  const data = response || [];
  return data;
};

export const saveBulkDisposition = async (items) => {
  const service = getService(ServiceName.rms_evidence_detail);
  const response = await service.patch(null, { items }, { query: { action: 'bulkDisposition' } });
  const data = response?.data || response;
  return data;
};

export const getEvidenceHelperDetails = async (evidenceId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_helper);
  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
      action: 'evidenceHelperFlag',
      clearanceLevel,
    },
  });
  const data = response;
  return data;
};

export const getNavBadges = async (agencyID, clearanceLevel, auditIntervalDays) => {
  const service = getService(ServiceName.rms_evidence_helper);

  return await service.find({
    query: {
      action: 'navBadges',
      agencyID,
      clearanceLevel,
      auditIntervalDays,
    },
  });
};

export const getEvidenceOffenses = async (ptsIncidentID, search = '') => {
  const service = getService(ServiceName.rms_evidence_helper);

  return await service.find({
    query: {
      action: 'offenses',
      ptsIncidentID,
      search,
    },
  });
};

export const updateEvidenceCurrentState = async (payload) => {
  const service = getService(ServiceName.rms_evidence_helper);

  return await service.patch(null, payload, {
    query: {
      action: 'updateEvidenceCurrentState',
    },
  });
};

export const updateEvidenceCurrentStateBulk = async (payloadArray) => {
  const service = getService(ServiceName.rms_evidence_helper);

  return await service.patch(null, payloadArray, {
    query: {
      action: 'updateEvidenceCurrentStateBulk',
    },
  });
};

export const createEvidenceNote = async (payload) => {
  const service = getService(ServiceName.rms_evidence_notes);
  const response = await service.create(payload);
  const data = response || [];
  return data;
};

export const editEvidenceNote = async (payload) => {
  const service = getService(ServiceName.rms_evidence_notes);
  const response = await service.patch(payload.ptsEvidenceNoteID, payload);
  const data = response || [];
  return data;
};

export const getEvidenceNotes = async (evidenceId, query = {}) => {
  const service = getService(ServiceName.rms_evidence_notes);
  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
      IsDeleted: false,
      $sort: {
        Created: -1,
      },
      ...query,
    },
  });
  const data = response?.data || [];
  return data;
};

export const deleteEvidence = async (id) => {
  const service = getService(ServiceName.rms_evidence_detail);
  const response = await service.remove(id);
  const data = response?.data || response;
  return data;
};

export const deleteEvidenceNote = async (evidenceNoteId) => {
  const service = getService(ServiceName.rms_evidence_notes);
  const response = await service.remove(evidenceNoteId);
  const data = response?.data || [];
  return data;
};

export const getEvidenceStorages = async (evidenceId) => {
  const service = getService(ServiceName.rms_evidence_storage);
  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
    },
  });

  const data = response || [];
  return data;
};

export const getLatestEvidenceStorage = async (evidenceId) => {
  const service = getService(ServiceName.rms_evidence_storage);

  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
      $limit: 1,
      $sort: {
        DateTimeIn: -1,
      },
    },
  });

  return response?.[0] || null;
};

export const createEvidenceStorage = async (payload) => {
  const service = getService(ServiceName.rms_evidence_storage);
  const response = await service.create(payload);
  return response?.data || response;
};
export const editEvidenceStorage = async (payload) => {
  const service = getService(ServiceName.rms_evidence_storage);
  const response = await service.patch(payload.ptsEvidenceStorageID, payload);
  return response?.data || response;
};

export const deleteEvidenceStorage = async (evidenceStorageId) => {
  const service = getService(ServiceName.rms_evidence_storage);
  const response = await service.remove(evidenceStorageId);
  const data = response?.data || [];
  return data;
};

export const getEvidenceCorrectionRequests = async (evidenceId) => {
  const service = getService(ServiceName.rms_evidence_correction);

  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
      IsDeleted: false,
      $sort: {
        Created: -1,
      },
    },
  });

  return response || [];
};

export const getMyCorrectionRequests = async () => {
  const service = getService(ServiceName.rms_evidence_correction);

  const response = await service.find({
    query: { action: 'routedToMe' },
  });

  return response || [];
};

export const getEvidenceCorrectionRequestDetails = async (id) => {
  const service = getService(ServiceName.rms_evidence_correction);

  const response = await service.get(id);

  return response || {};
};

export const createEvidenceCorrectionRequest = async (payload) => {
  const service = getService(ServiceName.rms_evidence_correction);

  const response = await service.create(payload);

  return response?.data || response;
};

export const editEvidenceCorrectionRequest = async (payload) => {
  const service = getService(ServiceName.rms_evidence_correction);

  const response = await service.patch(payload.ptsEvidenceCorrectionRequestID, payload);

  return response?.data || response;
};

export const deleteEvidenceCorrectionRequest = async (evidenceCorrectionRequestId) => {
  const service = getService(ServiceName.rms_evidence_correction);

  const response = await service.remove(evidenceCorrectionRequestId);

  return response?.data || response;
};

export const sendMailForOverdueRequest = async (evidenceIds) => {
  const service = getService(ServiceName.rms_evidence_records);
  const response = await service.create({
    action: 'sendMailForOverdueRequest',
    evidenceIds,
  });

  return response?.data || response;
};

export const getEvidenceCheckoutRequests = async (evidenceId) => {
  const service = getService(ServiceName.rms_evidence_checkout_request);

  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
      IsDeleted: false,
      $sort: {
        Created: -1,
      },
    },
  });

  return response?.data || response || [];
};

export const getEvidenceCheckoutRequestDetails = async (id) => {
  const service = getService(ServiceName.rms_evidence_checkout_request);

  const response = await service.get(id);

  return response || {};
};

export const createEvidenceCheckoutRequest = async (payload) => {
  const service = getService(ServiceName.rms_evidence_checkout_request);

  const response = await service.create(payload);

  return response?.data || response;
};

export const editEvidenceCheckoutRequest = async (payload) => {
  const service = getService(ServiceName.rms_evidence_checkout_request);

  const response = await service.patch(payload.ptsEvidenceRequestID, payload);

  return response?.data || response;
};

export const deleteEvidenceCheckoutRequest = async (id) => {
  const service = getService(ServiceName.rms_evidence_checkout_request);
  const response = await service.remove(id);
  return response?.data || response;
};

export const createParty = async (payload) => {
  const service = getService(ServiceName.rms_evidence_party_v2);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editParty = async (payload) => {
  const service = getService(ServiceName.rms_evidence_party_v2);
  const response = await service.patch(1, payload);
  return response?.data || response;
};

export const getPartyDetails = async (id, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_party_v2);
  const response = await service.get(id, { query: { clearanceLevel } });
  return response || {};
};

export const getEvidenceParties = async (evidenceId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_party_v2);
  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
      clearanceLevel,
    },
  });

  return response?.data || response || [];
};

export const deleteParty = async (EvidencePersonId) => {
  const service = getService(ServiceName.rms_evidence_party_v2);
  const response = await service.remove(EvidencePersonId);
  return response?.data || response;
};

export const createEvidenceCustody = async (payload) => {
  const service = getService(ServiceName.rms_evidence_custody);

  const response = await service.create(payload);

  return response?.data || response;
};

export const editEvidenceCustody = async (payload) => {
  const service = getService(ServiceName.rms_evidence_custody);

  const response = await service.patch(payload.ptsEvidenceCustodyID, payload);

  return response?.data || response;
};

export const getEvidenceCustodies = async (evidenceId) => {
  const service = getService(ServiceName.rms_evidence_custody);

  const response = await service.find({
    query: {
      ptsEvidenceID: evidenceId,
      IsDeleted: false,
      $limit: 0,
      $sort: {
        [EVIDENCE_CUSTODY_FIELDS.CreatedDate]: -1,
      },
    },
  });

  return response?.data || response;
};

export const deleteEvidenceCustody = async (custodyId) => {
  const service = getService(ServiceName.rms_evidence_custody);

  const response = await service.remove(custodyId);

  return response?.data || response;
};

export const searchEvidenceParty = async (searchText) => {
  const service = getService(ServiceName.Evi_Evidence_Party);

  return await service.find({
    query: {
      search: searchText,
    },
  });
};

export const getCorrectionOptions = (fieldName, idFieldName) => async ({
  search,
  offset,
  limit,
}) => {
  const service = getService(ServiceName.rms_evidence_corr_request);
  const response = await service.find({
    query: {
      action: 'getFilterDropdownData',
      field: fieldName,
      idField: idFieldName,
      search: search ?? '',
      $skip: offset,
      $limit: limit,
    },
  });

  return {
    items: (response?.data || []).map((row) => ({
      Id: row.Id,
      Code: row.Code,
      Description: row.Description,
    })),
    totalCount: response?.total || 0,
  };
};

export const checkInEvidence = async (payload, currentUserFullname) => {
  const { evidenceRefs = [], transactionType, latestStorage, ...custodyData } = payload;

  const service = getService(ServiceName.rms_evidence_custody);
  const recordedDate = new Date();
  const to = custodyData[EVIDENCE_CUSTODY_FIELDS.StorageLocation];
  const comeFromCheckInFlow = transactionType === CUSTODY_TRANSACTION_TYPE.Check_In;

  const custodyItems = await Promise.all(
    evidenceRefs.map(async (ref) => {
      const custodies = await getEvidenceCustodies(ref.ptsEvidenceID);
      const latestCustody = custodies?.[0];
      const from = latestCustody?.CustodyTo ?? null;

      return {
        ptsEvidenceCustodyID: null,
        ptsIncidentID: ref.ptsIncidentID ?? 0,
        ptsEvidenceID: ref.ptsEvidenceID,
        EvidenceID: ref.EvidenceID,
        Status: ref.Status,
        StatusID: ref.StatusID,
        CustodyFrom: from,
        CustodyTo: to,
        Reason: custodyData.Reason,
        Notes: custodyData.Notes,
        RequiredAction: custodyData.RequiredAction,
        RequiredActionID: custodyData.RequiredActionID,
        DisposalCode: custodyData.DisposalCode,
        DisposalCodeID: custodyData.DisposalCodeID,
        TransactionDate: custodyData.TransactionDate,
        TransactionType: transactionType,
        ComeFromCheckInFlow: comeFromCheckInFlow,
        SubLocation: custodyData.SubLocation,
        SubLocationID: custodyData.SubLocationID,
        Container: custodyData.Container,
        ContainerID: custodyData.ContainerID,
        StorageLocation: custodyData.StorageLocation,
        StorageLocationID: custodyData.StorageLocationID,
        RecordedBy: currentUserFullname,
        RecordedDate: recordedDate,
        PrintReceipt: custodyData.PrintReceipt,
      };
    })
  );
  const response = await service.create(custodyItems, {
    query: { IsBulk: true, currentUserFullname },
  });

  return response;
};

export const checkOutEvidence = async (payload, currentUserFullname) => {
  const { evidenceRefs = [], transactionType, ...custodyData } = payload;

  const service = getService(ServiceName.rms_evidence_custody);
  const recordedDate = new Date();
  const to = custodyData[EVIDENCE_CUSTODY_FIELDS.CustodyTo];
  const comeFromCheckInFlow = false;

  const custodyItems = await Promise.all(
    evidenceRefs.map(async (ref) => {
      const custodies = await getEvidenceCustodies(ref.ptsEvidenceID);
      const latestCustody = custodies?.[0];
      const from = latestCustody?.CustodyTo ?? null;

      return {
        ptsEvidenceCustodyID: null,
        ptsIncidentID: ref.ptsIncidentID ?? 0,
        ptsEvidenceID: ref.ptsEvidenceID,
        EvidenceID: ref.EvidenceID,
        Status: ref.Status,
        StatusID: ref.StatusID,

        CustodyFrom: from,
        CustodyTo: to,
        CustodyToPtsPersonID: custodyData[EVIDENCE_CUSTODY_FIELDS.CustodyToPtsPersonID],
        CustodyToEmail: custodyData[EVIDENCE_CUSTODY_FIELDS.CustodyToEmail],
        ExpectedReturnDate: custodyData[EVIDENCE_CUSTODY_FIELDS.ExpectedReturnDate],

        Reason: custodyData.Reason,
        Notes: custodyData.Notes,
        TransactionDate: custodyData.TransactionDate,
        TransactionType: transactionType,
        ComeFromCheckInFlow: comeFromCheckInFlow,
        RecordedBy: currentUserFullname,
        RecordedDate: recordedDate,
        PrintReceipt: custodyData.PrintReceipt,
      };
    })
  );

  const response = await service.create(custodyItems, {
    query: { IsBulk: true, currentUserFullname },
  });

  return response;
};

export const transferEvidence = async (payload, currentUserFullname) => {
  const { evidenceRefs = [], transactionType, ...custodyData } = payload;

  const service = getService(ServiceName.rms_evidence_custody);
  const recordedDate = new Date();
  const to = custodyData[EVIDENCE_CUSTODY_FIELDS.CustodyTo];
  const comeFromCheckInFlow = false;

  const custodyItems = await Promise.all(
    evidenceRefs.map(async (ref) => {
      const custodies = await getEvidenceCustodies(ref.ptsEvidenceID);
      const latestCustody = custodies?.[0];
      const from = latestCustody?.CustodyTo ?? null;

      return {
        ptsEvidenceCustodyID: null,
        ptsIncidentID: ref.ptsIncidentID ?? 0,
        ptsEvidenceID: ref.ptsEvidenceID,
        EvidenceID: ref.EvidenceID,
        Status: ref.Status,
        StatusID: ref.StatusID,

        CustodyFrom: from,
        CustodyTo: to,
        CustodyToPtsPersonID: custodyData[EVIDENCE_CUSTODY_FIELDS.CustodyToPtsPersonID],
        CustodyToEmail: custodyData[EVIDENCE_CUSTODY_FIELDS.CustodyToEmail],
        ExpectedReturnDate: custodyData[EVIDENCE_CUSTODY_FIELDS.ExpectedReturnDate],

        Reason: custodyData.Reason,
        Notes: custodyData.Notes,
        TransactionDate: custodyData.TransactionDate,
        TransactionType: transactionType,
        ComeFromCheckInFlow: comeFromCheckInFlow,
        RecordedBy: currentUserFullname,
        RecordedDate: recordedDate,
        PrintReceipt: custodyData.PrintReceipt,
      };
    })
  );

  const response = await service.create(custodyItems, {
    query: { IsBulk: true, currentUserFullname },
  });

  return response;
};

export const getAgencyOverdueDaysSetting = async (agencyId) => {
  const service = getService(ServiceName.settings_agency_rms);
  const rows = (await service.get(agencyId + ServiceName.RMS_Evidence_Audit_OverDueDay)) || [];

  return rows?.[0]?.ValueNumeric ?? null;
};

export const getDisposalRelevantEvidence = async (agencyId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const results = await service.find({
    query: {
      mode: 'disposal-relevant',
      agencyID: agencyId,
      clearanceLevel,
    },
  });

  return mapDisposalRelevantItems(results);
};

export const getMyDisposalRelevantEvidence = async (agencyId, personId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const results = await service.find({
    query: {
      mode: 'disposal-relevant',
      agencyID: agencyId,
      personID: personId,
      clearanceLevel,
    },
  });

  return mapDisposalRelevantItems(results);
};

export const getDisposalRelevantEvidenceItem = async (agencyId, ptsEvidenceID) => {
  const service = getService(ServiceName.rms_evidence_records);

  const results = await service.find({
    query: {
      mode: 'disposal-relevant',
      agencyID: agencyId,
      ptsEvidenceID,
    },
  });

  return mapDisposalRelevantItems(results)[0] || null;
};

// Powers the Analytics page's KPI stat tiles — server-side aggregate counts scoped by the
// current toolbar filters, so the FE never has to pull the full evidence set to compute them.
export const getEvidenceAnalyticsSummary = async (query) => {
  const service = getService(ServiceName.rms_evidence_records);

  const response = await service.find({
    query: {
      mode: 'analytics-summary',
      ...query,
    },
  });

  return response || null;
};

export const getEvidenceDashboardSummary = async (agencyId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const response = await service.find({
    query: {
      mode: 'dashboard-summary',
      agencyID: agencyId,
      clearanceLevel,
    },
  });

  return response || null;
};

export const getMyDashboardSummary = async (agencyId, personId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const response = await service.find({
    query: {
      mode: 'my-dashboard-summary',
      agencyID: agencyId,
      personID: personId,
      clearanceLevel,
    },
  });

  return response || null;
};

// Portal-only variant: scopes by SubmittedBy OR CollectedBy OR ReceivedBy, matching the
// reference Evidence Management portal. Kept separate from getMyDashboardSummary so the
// Main Dashboard's SubmittedBy-only summary is unaffected.
export const getPortalDashboardSummary = async (agencyId, personId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const response = await service.find({
    query: {
      mode: 'portal-dashboard-summary',
      agencyID: agencyId,
      personID: personId,
      clearanceLevel,
    },
  });

  return response || null;
};

export const getEvidenceCorrectionsReport = async (agencyId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const response = await service.find({
    query: {
      mode: 'corrections-report',
      agencyID: agencyId,
      clearanceLevel,
    },
  });

  return response || [];
};

export const getEvidenceAgencySummary = async (agencyId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const response = await service.find({
    query: {
      mode: 'agency-summary',
      agencyID: agencyId,
      clearanceLevel,
    },
  });

  return response || [];
};

export const getEvidenceAnalyticsBadges = async (agencyId, clearanceLevel) => {
  const service = getService(ServiceName.rms_evidence_records);

  const response = await service.find({
    query: {
      mode: 'sidebar-analytics-badges',
      agencyID: agencyId,
      clearanceLevel,
    },
  });

  return response || null;
};